import gql from 'graphql-tag';

import {
  InstantSearchSortOrder,
  type SolQueryParamsVariablesNew,
} from '@/components/InstantSearch';
import { QUERY_DEFAULTS, useSolQuery } from '@/hooks/useSolQuery';

import { fetcherSol } from '../sol-fetcher';
import {
  type AIVisibilitySettingsSaveInput,
  type CanaryFinalRecommendationsGQLResponse,
  type CanaryOverviewProductAreaScoresGQLResponse,
  type CanaryPromptRunResultsGQLResponse,
  type CanaryPromptRunResultsVariables,
  type CanaryVisibilityScoreByCompanyGQLResponse,
  type CanaryVisibilityScoreByProductAreaGQLResponse,
  type CanaryVisibilityScoreByProviderGQLResponse,
  type CanaryVisibilityScoreTrendGQLResponse,
  type CanaryVisibilityScoreTrendByCompanyTypeGQLResponse,
  type DiscoverProductAreasGQLResponse,
  type DiscoverProductAreasVariables,
  type GetProductAreasGQLResponse,
  type GenerateAEOSolutionGQLResponse,
  type InProgressAIVisibilityWorkflowGQLResponse,
  type LatestCompletedAIVisibilityWorkflowDateGQLResponse,
  type SaveProductAreasGQLResponse,
  type TriggerCanaryWorkflowGQLResponse,
} from './aiVisibilityType';

export const DISCOVER_PRODUCT_AREAS_COUNT = 20;

export const useLatestCompletedAIVisibilityWorkflowDate = (enabled: boolean) => {
  const { data, error, isLoading } = useSolQuery<
    LatestCompletedAIVisibilityWorkflowDateGQLResponse,
    SolQueryParamsVariablesNew
  >(
    {
      query: enabled
        ? gql`
            query LatestCompletedAIVisibilityWorkflowDate(
              $page: Pagination!
              $filter: [FilterParamInput]!
              $sort: [SortParamInput]!
            ) {
              aiVisibilityWorkflows {
                get(page: $page, filter: $filter, sort: $sort) {
                  edges {
                    node {
                      completedAt
                    }
                  }
                }
              }
            }
          `
        : false,
      variables: {
        page: { limit: 1, offset: 0 },
        filter: [
          { field: 'status', operator: 'eq', operand: ['complete'] },
          { field: 'gapAnalysisStatus', operator: 'eq', operand: ['complete'] },
          { field: 'snapshotStatus', operator: 'eq', operand: ['loaded'] },
        ],
        sort: [{ field: 'completedAt', direction: InstantSearchSortOrder.DESC }],
      },
    },
    {
      refetchInterval: (query) =>
        query.state.data?.aiVisibilityWorkflows?.get.edges?.[0]?.node?.completedAt != null
          ? QUERY_DEFAULTS.refetchInterval
          : 30_000,
    },
  );

  const completedWorkflowAt = data?.aiVisibilityWorkflows?.get.edges?.[0]?.node?.completedAt;

  return {
    completedAt: error ? undefined : completedWorkflowAt,
    hasCompletedWorkflow: completedWorkflowAt != null,
    isLoading: enabled && isLoading,
    error,
  };
};

export const useInProgressAIVisibilityWorkflow = (enabled: boolean) => {
  const { data, error, isLoading, mutate } = useSolQuery<
    InProgressAIVisibilityWorkflowGQLResponse,
    SolQueryParamsVariablesNew
  >(
    {
      query: enabled
        ? gql`
            query InProgressAIVisibilityWorkflow(
              $page: Pagination!
              $filter: [FilterParamInput]!
              $sort: [SortParamInput]!
            ) {
              aiVisibilityWorkflows {
                get(page: $page, filter: $filter, sort: $sort) {
                  edges {
                    node {
                      id
                    }
                  }
                }
              }
            }
          `
        : false,
      variables: {
        page: { limit: 1, offset: 0 },
        // eslint-disable-next-line lingui/no-unlocalized-strings -- Sol workflow status identifier.
        filter: [{ field: 'status', operator: 'eq', operand: ['in progress'] }],
        sort: [{ field: 'createdAt', direction: InstantSearchSortOrder.DESC }],
      },
    },
    { refetchInterval: 30_000 },
  );

  return {
    hasInProgressWorkflow:
      !isLoading && !error && data?.aiVisibilityWorkflows?.get.edges?.[0]?.node?.id != null,
    error,
    mutate,
  };
};

export const triggerCanaryWorkflow = async () => {
  const data = await fetcherSol<undefined, TriggerCanaryWorkflowGQLResponse>({
    query: gql`
      mutation TriggerCanaryWorkflow {
        canaryWorkflows {
          trigger {
            id
          }
        }
      }
    `,
  });

  return data?.canaryWorkflows?.trigger;
};

export const useCanaryVisibilityScoreByCompany = () => {
  const { data, isLoading, error } = useSolQuery<CanaryVisibilityScoreByCompanyGQLResponse>({
    query: gql`
      query CanaryVisibilityScoreByCompanyQuery {
        visualization {
          canaryVisibilityScoreByCompany {
            customer {
              id
              company
              domain
              scores {
                model
                score
              }
              overallScore
              change
            }
            competitors {
              id
              company
              domain
              scores {
                model
                score
              }
              overallScore
              change
            }
          }
        }
      }
    `,
  });

  return { scores: data?.visualization.canaryVisibilityScoreByCompany, isLoading, error };
};

export const useCanaryVisibilityScoreTrend = () => {
  const { data, isLoading, error } = useSolQuery<CanaryVisibilityScoreTrendGQLResponse>({
    query: gql`
      query CanaryVisibilityScoreTrendQuery {
        visualization {
          canaryVisibilityScoreTrend {
            company
            companyType
            data {
              sundayOfWeek
              visibilityScore
            }
          }
        }
      }
    `,
  });

  return { trends: data?.visualization.canaryVisibilityScoreTrend, isLoading, error };
};

export const useCanaryVisibilityScoreTrendByCompanyType = () => {
  const { data, isLoading, error } =
    useSolQuery<CanaryVisibilityScoreTrendByCompanyTypeGQLResponse>({
      query: gql`
        query CanaryVisibilityScoreTrendByCompanyType {
          visualization {
            canaryVisibilityScoreTrendByCompanyType {
              companyType
              data {
                sundayOfWeek
                visibilityScore
              }
            }
          }
        }
      `,
    });

  return { trends: data?.visualization.canaryVisibilityScoreTrendByCompanyType, isLoading, error };
};

export const useCanaryVisibilityScoreByProductArea = () => {
  const { data, isLoading, error } = useSolQuery<CanaryVisibilityScoreByProductAreaGQLResponse>({
    query: gql`
      query CanaryVisibilityScoreByProductAreaQuery {
        visualization {
          canaryVisibilityScoreByProductArea {
            productArea
            categoryLeader
            companies {
              company
              domain
              visibilityScore
            }
          }
        }
      }
    `,
  });

  return { scores: data?.visualization.canaryVisibilityScoreByProductArea, isLoading, error };
};

export const useCanaryOverviewProductAreaScores = () => {
  const { data, isLoading, error } = useSolQuery<CanaryOverviewProductAreaScoresGQLResponse>({
    query: gql`
      query CanaryOverviewProductAreaScoresQuery {
        visualization {
          canaryVisibilityScoreByProductArea {
            productArea
            competitorAverageScore
            customerAverageScore
            customerChange
          }
        }
      }
    `,
  });

  return { scores: data?.visualization.canaryVisibilityScoreByProductArea, isLoading, error };
};

export const useCanaryVisibilityScoreByProvider = () => {
  const { data, isLoading, error } = useSolQuery<CanaryVisibilityScoreByProviderGQLResponse>({
    query: gql`
      query CanaryVisibilityScoreByProviderQuery {
        visualization {
          canaryVisibilityScoreByProvider {
            provider
            averageScore
          }
        }
      }
    `,
  });

  return { scores: data?.visualization.canaryVisibilityScoreByProvider, isLoading, error };
};

export const useCanaryFinalRecommendations = () => {
  const { data, isLoading, error, cancel, mutate } =
    useSolQuery<CanaryFinalRecommendationsGQLResponse>({
      query: gql`
        query CanaryFinalRecommendations {
          visualization {
            canaryFinalRecommendations {
              workflow {
                summary
              }
              recommendations {
                id
                title
                impact
                priority
                gapArea
                diagnosis
                action
                purpose
                solutionMarkdown
                scopeType
                promptResults {
                  promptId
                  promptText
                }
              }
            }
          }
        }
      `,
    });

  return {
    summary: data?.visualization.canaryFinalRecommendations?.workflow?.summary,
    recommendations: data?.visualization.canaryFinalRecommendations?.recommendations,
    isLoading,
    error,
    cancel,
    mutate,
  };
};

export const generateAEOSolution = async (recommendationId: string): Promise<string> => {
  const data = await fetcherSol<{ recommendationId: string }, GenerateAEOSolutionGQLResponse>({
    query: gql`
      mutation GenerateAEOSolution($recommendationId: String!) {
        generateAEOSolution(recommendationId: $recommendationId)
      }
    `,
    variables: { recommendationId },
  });

  return data.generateAEOSolution;
};

export const useCanaryPromptRunResults = (promptIds?: string[]) => {
  const { data, isLoading, error } = useSolQuery<
    CanaryPromptRunResultsGQLResponse,
    CanaryPromptRunResultsVariables
  >({
    query: gql`
      query CanaryPromptRunResults($promptIds: [String!]) {
        visualization {
          canaryPromptRunResults(promptIds: $promptIds) {
            id
            promptId
            promptType
            provider
            model
            promptText
            responseText
          }
        }
      }
    `,
    variables: { promptIds },
  });

  return { promptResults: data?.visualization.canaryPromptRunResults, isLoading, error };
};

export const useAIVisibilitySettings = () => {
  const { data, isLoading, error, mutate } = useSolQuery<GetProductAreasGQLResponse>({
    query: gql`
      query getProductAreas {
        productAreas {
          get {
            brandProductAreas
            competitors {
              name
              domain
              comparedProductAreas
            }
          }
        }
      }
    `,
  });

  return {
    settings: data?.productAreas.get,
    isLoading,
    error,
    mutate,
  };
};

export const saveAIVisibilitySettings = async (input: AIVisibilitySettingsSaveInput) => {
  const data = await fetcherSol<AIVisibilitySettingsSaveInput, SaveProductAreasGQLResponse>({
    query: gql`
      mutation saveProductAreas(
        $brandProductAreas: [String!]!
        $competitors: [ProductAreaCompetitorInput!]!
        $isOverride: Boolean! = false
      ) {
        productAreas {
          save(
            brandProductAreas: $brandProductAreas
            competitors: $competitors
            isOverride: $isOverride
          ) {
            success
          }
        }
      }
    `,
    variables: input,
  });

  return data.productAreas.save;
};

let inFlightDiscoverProductAreas: { count: number; promise: Promise<string[]> } | null = null;

export const discoverProductAreas = async (
  count = DISCOVER_PRODUCT_AREAS_COUNT,
): Promise<string[]> => {
  if (inFlightDiscoverProductAreas?.count === count) {
    return inFlightDiscoverProductAreas.promise;
  }

  const promise = (async () => {
    const data = await fetcherSol<DiscoverProductAreasVariables, DiscoverProductAreasGQLResponse>({
      query: gql`
        mutation discoverProductAreas($count: Int!) {
          productAreas {
            discover(count: $count)
          }
        }
      `,
      variables: { count },
    });

    return data.productAreas.discover;
  })();

  inFlightDiscoverProductAreas = { count, promise };

  try {
    return await promise;
  } finally {
    if (inFlightDiscoverProductAreas?.promise === promise) {
      inFlightDiscoverProductAreas = null;
    }
  }
};

export const resetDiscoverProductAreas = () => {
  inFlightDiscoverProductAreas = null;
};

export const competitorNameFromDomain = (domain: string): string => {
  const host = domain
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .split('/')[0];
  const label = host?.split('.')[0] ?? '';

  if (!label) {
    return '';
  }

  return `${label.charAt(0).toUpperCase()}${label.slice(1)}`;
};
